<template>
  <div>
    <SkinnyBanner data-test="home-skinny-banner" page="home" />
    <!-- Short term solution to have a H1 for SEO and A11y purpose on the home page -->
    <!-- Todo: design a homepage element to include the H1 -->
    <!-- see [LIF-1601](https://backmarket.atlassian.net/browse/LIF-1601) -->
    <h1 class="sr-only">{{ homeData.title }}</h1>
    <meta :content="COMPANY_NAME" itemprop="name" />
    <meta :content="homeData.meta.image.src" itemprop="logo" />
    <TheMainContent>
      <div class="min-h-[100vh]">
        <!-- We display no placeholder component -->
        <!-- The content, style and order of these block can change at any moment -->
        <!-- Set a min height of viewport height, to avoid a flash of footer display -->
        <Container
          v-for="block in blocksToRender"
          :key="block.id"
          :is-full-width="block.isFullWidth"
        >
          <BlockRenderer
            :id="block.id"
            :block-position="block.blockPosition"
            :data-test="block.id"
            :hidden-devices="block.hiddenDevices"
            :props="block.props"
            :type="block.type"
          />
        </Container>
      </div>
    </TheMainContent>
    <RevContainer v-if="navigationData">
      <HomeNavigation :navigation-seo="navigationData" />
    </RevContainer>
  </div>
</template>

<script lang="ts" setup>
import { useHead, useRequestURL, useRuntimeConfig } from '#imports'
import { computed } from 'vue'

import { getHomeNavigationContent } from '@backmarket/http-api/src/api-specs-content'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { RevContainer } from '@ds/components/Container'
import { useRoute } from 'vue-router'

import TheMainContent from '~/components/TheMainContent/TheMainContent.vue'
import SkinnyBanner from '~/scopes/branding/components/SkinnyBanner/SkinnyBanner.vue'

import { useCMSAlternateLinks } from '../cms/composables/useCMSAlternateLinks'
import { useCMSMeta } from '../cms/composables/useCMSMeta'
import { useFetchContent } from '../cms/composables/useFetchPageContent'
import { createSocialMediaTagsFor } from '../cms/helpers/meta/meta'
import BlockRenderer from '../cms/shared-components/BlockRenderer/BlockRenderer.vue'
import Container from '../cms/shared-components/Container.vue'

import translations from './Home.translations'
import HomeNavigation from './HomeNavigation/HomeNavigation.vue'
import { FULL_WIDTH_COMPONENTS } from './constants'
import { assertsHomeDataExists } from './utils/assertsHomeDataExists'
import { useHomeSchema } from './utils/composables/useHomeSchema'
import { useHomeSocialMeta } from './utils/composables/useHomeSocialMeta'
import { getHtmlAttrsByCountry } from './utils/getHtmlAttrsByCountry'

const { data: homeData, error: homeError } = await useFetchContent()
const { data: navigationData } = await useHttpFetch(getHomeNavigationContent)

assertsHomeDataExists(homeData, homeError)

const blocksToRender = computed(() =>
  homeData.value.blocks.map((block, index) => ({
    id: block.id,
    isFullWidth: FULL_WIDTH_COMPONENTS.includes(block.type),
    props: block.props,
    hiddenDevices: block.hiddenDevices,
    blockPosition: index + 1,
    type: block.type,
  })),
)

const {
  market: { countryCode },
} = useMarketplace()

const meta = useCMSMeta(homeData)
const i18n = useI18n()
const route = useRoute()
const runtimeConfig = useRuntimeConfig()
const { COMPANY_NAME } = runtimeConfig.public
const alternateLinks = useCMSAlternateLinks(homeData)
const script = useHomeSchema(COMPANY_NAME)
const { hostname, protocol } = useRequestURL()
const socials = useHomeSocialMeta()

useHead({
  title: homeData.value.titleSeo,
  titleTemplate: i18n(translations.metaTitleTemplate),
  htmlAttrs: getHtmlAttrsByCountry(countryCode),
  link: [
    ...alternateLinks,
    {
      rel: 'canonical',
      href: `${protocol}//${hostname}${route.path}`,
    },
  ],
  meta: [...meta, ...createSocialMediaTagsFor(socials)],
  script,
})
</script>
